import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { TrendingUp, Clock, CheckCircle, DollarSign } from "lucide-react";

export default function Analytics() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <h1 className="text-2xl font-heading font-bold">Healion.ai</h1>
            <nav className="hidden md:flex gap-6">
              <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground">
                Dashboard
              </Link>
              <Link to="/errors" className="text-sm text-muted-foreground hover:text-foreground">
                Errors
              </Link>
              <Link to="/connections" className="text-sm text-muted-foreground hover:text-foreground">
                Connections
              </Link>
              <Link to="/analytics" className="text-sm font-medium border-b-2 border-foreground pb-1">
                Analytics
              </Link>
              <Link to="/settings" className="text-sm text-muted-foreground hover:text-foreground">
                Settings
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold mb-2">Analytics</h1>
            <p className="text-muted-foreground">Track your team's performance and ROI</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline">Last 7 days</Button>
            <Button variant="outline">Last 30 days</Button>
            <Button variant="outline">Last 90 days</Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 border-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Time Saved</span>
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-3xl font-heading font-bold">124 hrs</div>
            <p className="text-xs text-muted-foreground mt-1">↑ 32% from last month</p>
          </Card>

          <Card className="p-6 border-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Fixes Applied</span>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-3xl font-heading font-bold">247</div>
            <p className="text-xs text-muted-foreground mt-1">94% acceptance rate</p>
          </Card>

          <Card className="p-6 border-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Cost Savings</span>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-3xl font-heading font-bold">$18.6K</div>
            <p className="text-xs text-muted-foreground mt-1">Based on $150/hr rate</p>
          </Card>

          <Card className="p-6 border-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">ROI</span>
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-3xl font-heading font-bold">18.8x</div>
            <p className="text-xs text-muted-foreground mt-1">Return on investment</p>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="p-6 border-2">
            <h2 className="text-xl font-heading font-bold mb-4">Fixes Over Time</h2>
            <div className="h-64 flex items-center justify-center border-2 border-dashed rounded-lg">
              <p className="text-muted-foreground">Chart placeholder</p>
            </div>
          </Card>

          <Card className="p-6 border-2">
            <h2 className="text-xl font-heading font-bold mb-4">Acceptance Rate</h2>
            <div className="h-64 flex items-center justify-center border-2 border-dashed rounded-lg">
              <p className="text-muted-foreground">Chart placeholder</p>
            </div>
          </Card>
        </div>

        {/* Detailed Stats */}
        <Card className="p-6 border-2">
          <h2 className="text-xl font-heading font-bold mb-4">Detailed Statistics</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <p className="font-medium">Average fix time</p>
                <p className="text-sm text-muted-foreground">Time from error to fix</p>
              </div>
              <span className="text-2xl font-heading font-bold">4.2 min</span>
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <p className="font-medium">Developer time saved per fix</p>
                <p className="text-sm text-muted-foreground">Estimated manual fix time</p>
              </div>
              <span className="text-2xl font-heading font-bold">30 min</span>
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <p className="font-medium">Cost per fix</p>
                <p className="text-sm text-muted-foreground">Including AI and infrastructure</p>
              </div>
              <span className="text-2xl font-heading font-bold">$0.40</span>
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <p className="font-medium">Manual cost per fix</p>
                <p className="text-sm text-muted-foreground">Developer time at $150/hr</p>
              </div>
              <span className="text-2xl font-heading font-bold">$75</span>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}
